/**
 * Request and response shapes shared by the API and the web client.
 *
 * Anything here is part of the wire contract: changing it is a breaking change
 * for clients that are already deployed, which includes offline POS terminals
 * holding queued requests. Server-only shapes (Prisma models, internal service
 * results) do not belong in this package.
 */

import { z } from 'zod';

import type { Page } from './pagination';

// --- authentication ---------------------------------------------------------

/**
 * Login accepts a password or a PIN, never both.
 *
 * `refine` rather than a union so the error lands on the field the user can
 * actually fix, instead of a "must match exactly one schema in the union" error
 * at the top level.
 */
export const loginSchema = z
  .object({
    identifier: z.string().min(1).max(255),
    password: z.string().max(200).optional(),
    pin: z
      .string()
      .regex(/^\d{4,8}$/, 'PIN must be 4 to 8 digits.')
      .optional(),
    deviceCode: z.string().max(40).optional(),
    deviceId: z.string().uuid().optional(),
  })
  .refine((value) => Boolean(value.password) !== Boolean(value.pin), {
    message: 'Provide either a password or a PIN, not both.',
    path: ['password'],
  });

export type LoginInput = z.infer<typeof loginSchema>;

/**
 * Password change request.
 *
 * Shared rather than declared per-client so the browser form and the API agree
 * on the rules by construction: a length the client enforces but the server does
 * not is a rule that exists only until someone calls the endpoint directly.
 *
 * `newPassword` composition rules live here rather than in the controller so the
 * message reaches the user on the field they can fix. Minimum length is 12 rather
 * than the usual 8 because a seeded ERP credential is likely to be reused from
 * another system, and because the value must survive any future offline-syncing
 * till without truncation.
 */
export const PASSWORD_MIN_LENGTH = 12;

export const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Enter your current password.').max(200),
    newPassword: z
      .string()
      .min(PASSWORD_MIN_LENGTH, `Use at least ${PASSWORD_MIN_LENGTH} characters.`)
      .max(200, 'Passwords are limited to 200 characters.')
      .refine((v) => /[a-z]/.test(v), 'Include a lowercase letter.')
      .refine((v) => /[A-Z]/.test(v), 'Include an uppercase letter.')
      .refine((v) => /\d/.test(v), 'Include a digit.'),
    confirmPassword: z.string().min(1, 'Repeat the new password.'),
  })
  // Cross-field, so it is checked after the per-field rules above have passed:
  // a mismatch reported alongside three composition complaints is harder to act
  // on than the mismatch alone.
  .refine((value) => value.newPassword === value.confirmPassword, {
    message: 'The two passwords do not match.',
    path: ['confirmPassword'],
  })
  // Declared here rather than in the API so the form can disable its own submit
  // button. The API still enforces it; this is a convenience, not the control.
  .refine((value) => value.currentPassword !== value.newPassword, {
    message: 'The new password must differ from the current one.',
    path: ['newPassword'],
  });

export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;

export interface AuthSession {
  accessToken: string;
  refreshToken: string;
  /** Seconds until `accessToken` expires; the client refreshes proactively. */
  expiresIn: number;
  user: SessionUser;
}

export interface SessionUser {
  id: string;
  username: string;
  fullName: string;
  companyId: string;
  tenantId: string | null;
  defaultBranchId: string | null;
  language: Locale;
  roleCodes: string[];
  permissions: string[];
  /**
   * True while the account still holds its seeded or reset password. The API
   * issues tokens either way — refusing to log in would lock an operator out of
   * the one screen that can change it — but the client is expected to send them
   * straight to the change-password screen and allow nothing else until it is
   * done. `users.must_change_password` is the source of this flag.
   */
  mustChangePassword: boolean;
}

// --- locale -----------------------------------------------------------------

export const LOCALES = ['ar', 'en'] as const;
export type Locale = (typeof LOCALES)[number];
export const DEFAULT_LOCALE: Locale = 'ar';

/** Arabic is the primary language (spec §15.1); English is the fallback. */
export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value);
}

export function directionFor(locale: Locale): 'rtl' | 'ltr' {
  return locale === 'ar' ? 'rtl' : 'ltr';
}

// --- branches ---------------------------------------------------------------

export const branchInputSchema = z.object({
  code: z.string().min(1).max(20),
  nameAr: z.string().min(1).max(120),
  nameEn: z.string().max(120).optional(),
  phone: z.string().max(30).optional(),
  address: z.string().max(240).optional(),
  isActive: z.boolean().default(true),
});
export type BranchInput = z.infer<typeof branchInputSchema>;

export const branchUpdateSchema = branchInputSchema
  .partial()
  .refine((value) => Object.keys(value).length > 0, {
    message: 'Provide at least one field to update.',
  });
export type BranchUpdate = z.infer<typeof branchUpdateSchema>;

export interface Branch {
  id: string;
  code: string;
  nameAr: string;
  nameEn: string | null;
  phone: string | null;
  address: string | null;
  isActive: boolean;
  /** Present only when the caller explicitly asked for tombstones. */
  deletedAt?: string | null;
}

// --- items ------------------------------------------------------------------

export const ITEM_TYPES = [
  'STOCK',
  'SERVICE',
  'BUNDLE',
  'COMPOSITE',
  'EXPENSE',
  'FIXED_ASSET',
] as const;
export type ItemType = (typeof ITEM_TYPES)[number];

export const ITEM_STATUSES = ['ACTIVE', 'INACTIVE', 'DISCONTINUED', 'SELL_UNTIL_OUT'] as const;
export type ItemStatus = (typeof ITEM_STATUSES)[number];

/**
 * Query-string booleans arrive as `"true"`/`"1"`. Coercing with
 * `Boolean(value)` would make `"false"` truthy — a filter that shows deleted
 * rows when the user asked to exclude them.
 */
export const queryBoolean = z
  .union([z.boolean(), z.enum(['true', 'false', '1', '0'])])
  .transform((value) => value === true || value === 'true' || value === '1');

export const itemListQuerySchema = z.object({
  search: z.string().max(120).optional(),
  categoryId: z.string().uuid().optional(),
  brandId: z.string().uuid().optional(),
  type: z.enum(ITEM_TYPES).optional(),
  status: z.enum(ITEM_STATUSES).optional(),
  /** Requires the audit permission; gated server-side, not by this schema. */
  includeDeleted: queryBoolean.optional(),
});
export type ItemListQuery = z.infer<typeof itemListQuerySchema>;

export interface ItemSummary {
  id: string;
  code: string;
  nameAr: string;
  nameEn: string | null;
  shortName: string | null;
  type: ItemType;
  status: ItemStatus;
  baseUomId: string;
  taxGroupId: string | null;
  /** Decimal as a string: JSON numbers are IEEE-754 doubles and would lose cents. */
  averageCost: string;
  category: { id: string; nameAr: string } | null;
  brand: { id: string; nameAr: string } | null;
  barcodes: { barcode: string; uomId: string | null; isPrimary: boolean }[];
  imageUrl: string | null;
  version: number;
}

export interface ItemUom {
  uomId: string;
  uomCode: string;
  conversionFactor: string;
  isPurchasable: boolean;
  isSaleable: boolean;
}

export interface ItemDetail extends ItemSummary {
  description: string | null;
  manufacturer: string | null;
  countryOfOrigin: string | null;
  lastPurchaseCost: string | null;
  standardCost: string | null;
  minStock: string | null;
  maxStock: string | null;
  reorderPoint: string | null;
  reorderQty: string | null;
  trackBatch: boolean;
  trackSerial: boolean;
  trackExpiry: boolean;
  expiryAlertDays: number | null;
  governmentItemCode: string | null;
  governmentItemNameAr: string | null;
  defaultSupplierId: string | null;
  defaultWarehouseId: string | null;
  uoms: ItemUom[];
  createdAt: string;
  updatedAt: string;
}

export interface BarcodeHit {
  id: string;
  code: string;
  nameAr: string;
  unitId: string | null;
}

// --- health -----------------------------------------------------------------

export interface HealthReport {
  status: 'ok' | 'degraded';
  uptimeSeconds: number;
  timestamp: string;
}

export interface ReadinessReport {
  status: 'ok' | 'degraded';
  checks: Record<string, { status: 'up' | 'down'; latencyMs: number; detail?: string }>;
}

export interface VersionReport {
  name: string;
  deploymentMode: string;
  nodeEnv: string;
  apiPrefix: string;
}

// --- re-exported page shape -------------------------------------------------

export type { Page };
